// Project-root resolution for the `ss dev` commands.
//
// WHY THIS EXISTS: the dev cache (`.claude/config/dev-run.json`) used to be a
// path RELATIVE to `process.cwd()`. So `ss dev stop`/`start`/`wait` only found
// the cache when run from the exact project root — run them from `web/<app>` or
// `src/<App>.Api` (as an agent naturally does while building) and the cache
// "vanished" ("No cache found"). That made `stop` a no-op, so the previously
// launched servers lingered as zombies holding the ports, and the next launch
// could not bind → readiness timed out. Anchoring every cache path to the
// project root (found by walking UP from cwd) makes `ss dev` work from anywhere
// inside the project tree.
//
// Pure fs + path only — no command/commander coupling — so it is unit-testable.

import { existsSync, readdirSync } from 'fs';
import { join, dirname } from 'path';
import { homedir } from 'os';

/**
 * Locate the .NET API directory at `baseDir`: a `src/*.Api` (or `*Api`) folder
 * that has a `.csproj` AND an `appsettings.json`, else `baseDir` itself when it
 * holds an `appsettings.json` (flat layout). Returns the absolute path or null.
 */
export function detectApiDir(baseDir: string): string | null {
  const srcDir = join(baseDir, 'src');
  if (existsSync(srcDir)) {
    try {
      const entries = readdirSync(srcDir, { withFileTypes: true });
      const apiCandidates = entries.filter(
        (e) => e.isDirectory() && (e.name.endsWith('.Api') || e.name.endsWith('Api'))
      );
      for (const apiEntry of apiCandidates) {
        const candidate = join(srcDir, apiEntry.name);
        // Require a .csproj to avoid matching unrelated dirs (ExternalApi, MockApi, …)
        const hasCsproj = readdirSync(candidate).some((f) => f.endsWith('.csproj'));
        if (hasCsproj && existsSync(join(candidate, 'appsettings.json'))) {
          return candidate;
        }
      }
    } catch { /* ignore */ }
  }
  if (existsSync(join(baseDir, 'appsettings.json'))) {
    return baseDir;
  }
  return null;
}

/**
 * Locate the frontend directory at `baseDir`: `web/<sub>` (preferring one with a
 * vite.config when multiple), `web/`, `client|frontend|ui/`, or `src/web/` — the
 * first that has a `package.json`. Returns the absolute path or null.
 */
export function detectWebDir(baseDir: string): string | null {
  // 1) web/<any-subfolder>/package.json — accept ANY subdirectory of web/
  const webDir = join(baseDir, 'web');
  if (existsSync(webDir)) {
    try {
      const entries = readdirSync(webDir, { withFileTypes: true });
      const candidates = entries
        .filter((e) => e.isDirectory() && !e.name.startsWith('.') && e.name !== 'node_modules')
        .map((e) => join(webDir, e.name))
        .filter((c) => existsSync(join(c, 'package.json')));

      if (candidates.length === 1) return candidates[0];
      if (candidates.length > 1) {
        // Multi-app: prefer the candidate with a vite.config.* (SmartStack convention)
        const withVite = candidates.find((c) =>
          readdirSync(c).some((f) => f.startsWith('vite.config'))
        );
        return withVite ?? candidates[0];
      }
    } catch { /* ignore */ }
  }

  // 2) web/package.json (monorepo style)
  if (existsSync(join(webDir, 'package.json'))) return webDir;

  // 3) client/, frontend/, ui/ — alternative conventions
  for (const folderName of ['client', 'frontend', 'ui']) {
    const altPath = join(baseDir, folderName);
    if (existsSync(join(altPath, 'package.json'))) return altPath;
  }

  // 4) src/web/
  const srcWeb = join(baseDir, 'src', 'web');
  if (existsSync(join(srcWeb, 'package.json'))) return srcWeb;

  return null;
}

/**
 * Walk UP from `startDir` to the NEAREST directory that holds BOTH a detectable
 * API dir and a web dir — the SmartStack project root. Requiring both prevents
 * matching a bare `*.Api` folder (which has appsettings.json but no web sibling).
 *
 * Bounded by the home directory: the home dir (and anything above it) is never
 * treated as a project root, so when run from OUTSIDE any project the walk can't
 * escape into a stray ancestor that happens to contain `src/*.Api` + `web/`
 * (instead it returns null → the caller falls back to cwd). This ceiling is inert
 * for projects that don't live under `~` (e.g. on another drive). Returns the
 * absolute root, or null if none is found.
 */
export function findProjectRoot(startDir: string): string | null {
  const home = homedir();
  let dir = startDir;
  for (let i = 0; i < 12; i++) {
    if (dir === home) break; // don't treat the home dir (or above) as a project root
    if (detectApiDir(dir) && detectWebDir(dir)) return dir;
    const parent = dirname(dir);
    if (parent === dir) break; // reached the filesystem root
    dir = parent;
  }
  return null;
}
